/**
 * AI Email Shortcuts Toolbar
 * Provides AI-powered actions for emails
 * SAFETY: All communications saved as drafts - never auto-sent
 */
import React, { useState } from 'react';
import {
  UserPlus, MessageSquare, Mail, ClipboardList, FolderKanban,
  Users, FileText, Zap, BookOpen, Loader2, Check, X, Send,
  ChevronDown
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const shortcuts = [
  { id: 'onboarding', label: 'Create Client Onboarding', icon: UserPlus, color: 'text-blue-400 hover:bg-blue-500/20' },
  { id: 'slack', label: 'Draft Slack Announcement', icon: MessageSquare, color: 'text-purple-400 hover:bg-purple-500/20' },
  { id: 'reply', label: 'Draft Email Reply', icon: Mail, color: 'text-green-400 hover:bg-green-500/20' },
  { id: 'actions', label: 'Extract Action Items', icon: ClipboardList, color: 'text-yellow-400 hover:bg-yellow-500/20' },
  { id: 'project', label: 'Create Project', icon: FolderKanban, color: 'text-orange-400 hover:bg-orange-500/20' },
  { id: 'assign', label: 'Assign Team', icon: Users, color: 'text-cyan-400 hover:bg-cyan-500/20' },
  { id: 'template', label: 'Save as Template', icon: FileText, color: 'text-pink-400 hover:bg-pink-500/20' },
  { id: 'quick', label: 'Quick Reply', icon: Zap, color: 'text-amber-400 hover:bg-amber-500/20' },
  { id: 'knowledge', label: 'Save to Knowledge Base', icon: BookOpen, color: 'text-indigo-400 hover:bg-indigo-500/20' },
];

export default function AIEmailShortcuts({ 
  email, 
  userId, 
  onDraftCreated,
  onActionItemsExtracted,
  slackChannels = []
}) {
  const [loading, setLoading] = useState(null);
  const [showResult, setShowResult] = useState(null);
  const [resultData, setResultData] = useState(null);
  const [selectedChannel, setSelectedChannel] = useState('');
  const [editedContent, setEditedContent] = useState('');
  const [expanded, setExpanded] = useState(true);

  if (!email) return null;

  const handleShortcut = async (shortcutId) => {
    setLoading(shortcutId);
    setShowResult(null);
    setResultData(null);

    try {
      const payload = {
        email_id: email.id,
        subject: email.subject || '',
        sender: email.from || email.sender || '',
        body: email.body || email.text || email.snippet || '',
        html_body: email.html_body || '',
        user_id: userId,
      };

      switch (shortcutId) {
        case 'onboarding': {
          const res = await fetch(`${API}/api/email-ai/analyze/onboarding`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
          const data = await res.json();
          if (data.ok) {
            setResultData({
              type: 'onboarding',
              extraction: data.extraction,
              slackAnnouncement: data.slack_announcement,
            });
            setEditedContent(data.slack_announcement);
            setShowResult('onboarding');
            toast.success('Client info extracted successfully');
          }
          break;
        }

        case 'slack': {
          // First extract onboarding info, then show Slack draft
          const res = await fetch(`${API}/api/email-ai/analyze/onboarding`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
          const data = await res.json();
          if (data.ok) {
            setResultData({
              type: 'slack',
              content: data.slack_announcement,
            });
            setEditedContent(data.slack_announcement);
            setShowResult('slack');
            toast.success('Slack draft generated');
          }
          break;
        }

        case 'reply': {
          const res = await fetch(`${API}/api/email-ai/analyze/draft-reply`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
          const data = await res.json();
          if (data.ok || data.draft_reply) {
            setResultData({
              type: 'reply',
              content: data.draft_reply,
              subject: data.suggested_subject,
            });
            setEditedContent(data.draft_reply);
            setShowResult('reply');
            toast.success('Reply draft generated');
          }
          break;
        }

        case 'actions': {
          const res = await fetch(`${API}/api/email-ai/analyze/extract-actions`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
          const data = await res.json();
          if (data.ok) {
            setResultData({
              type: 'actions',
              items: data.action_items || [],
            });
            setShowResult('actions');
            if (onActionItemsExtracted) {
              onActionItemsExtracted(data.action_items);
            }
            toast.success(`${data.action_items?.length || 0} action items extracted`);
          }
          break;
        }

        case 'quick': {
          const res = await fetch(`${API}/api/email-ai/analyze/quick-reply`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
          const data = await res.json();
          if (data.ok && data.is_listing_report) {
            setResultData({
              type: 'quick',
              content: data.suggested_reply,
              urls: data.detected_urls,
              confidence: data.confidence,
            });
            setEditedContent(data.suggested_reply);
            setShowResult('quick');
            toast.success('Quick reply ready');
          } else {
            toast.info('This email doesn\'t appear to be a listing report');
          }
          break;
        }

        case 'template': {
          setResultData({
            type: 'template',
            content: email.body || email.text || '',
            subject: email.subject,
          });
          setShowResult('template');
          break;
        }

        case 'knowledge': {
          // Save to knowledge base
          try {
            const kbRes = await fetch(`${API}/api/knowledge-base`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                title: email.subject || 'Email Note',
                content: `From: ${email.from || email.sender}\nDate: ${email.date}\n\n${email.body || email.text || email.snippet}`,
                source: 'email',
                source_id: email.id,
                user_id: userId,
              }),
            });
            if (kbRes.ok) {
              toast.success('Saved to Knowledge Base');
            }
          } catch (e) {
            toast.error('Failed to save to Knowledge Base');
          }
          break;
        }

        case 'project':
        case 'assign':
          toast.info('Feature coming soon');
          break;

        default:
          break;
      }
    } catch (error) {
      console.error('Shortcut error:', error);
      toast.error('Failed to process shortcut');
    } finally {
      setLoading(null);
    }
  };

  const saveDraft = async (type, content, channel = null) => {
    try {
      const draftPayload = {
        type: type === 'slack' ? 'slack' : 'email',
        title: type === 'slack' 
          ? `Slack: ${resultData?.extraction?.client_name || 'New Client'}` 
          : `Re: ${email.subject}`,
        content: content,
        recipient: type === 'email' ? (email.from || email.sender) : null,
        channel: channel || selectedChannel || null,
        channel_name: slackChannels.find(c => c.id === (channel || selectedChannel))?.name || null,
        subject: type === 'email' ? `Re: ${email.subject}` : null,
        source_email_id: email.id,
        metadata: {
          original_subject: email.subject,
          original_sender: email.from || email.sender,
        },
        user_id: userId,
      };

      const res = await fetch(`${API}/api/email-ai/drafts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(draftPayload),
      });

      const data = await res.json();
      if (data.ok) {
        toast.success('Draft saved! Find it in Draft Center.');
        setShowResult(null);
        if (onDraftCreated) {
          onDraftCreated(data.draft);
        }
      }
    } catch (error) {
      console.error('Save draft error:', error);
      toast.error('Failed to save draft');
    }
  };

  const saveTemplate = async (name) => {
    if (!name) {
      toast.error('Please enter a template name');
      return;
    }

    try {
      const res = await fetch(`${API}/api/email-ai/templates?user_id=${userId}&name=${encodeURIComponent(name)}&content=${encodeURIComponent(resultData.content)}&type=email&subject=${encodeURIComponent(resultData.subject || '')}`, {
        method: 'POST',
      });
      const data = await res.json();
      if (data.ok) {
        toast.success('Template saved!');
        setShowResult(null);
      }
    } catch (error) {
      toast.error('Failed to save template');
    }
  };

  return (
    <div className="border-b border-white/20 bg-black/50">
      {/* Toolbar Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-2 hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-yellow-400" />
          <span className="text-sm font-medium text-white">AI Shortcuts</span>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {/* Shortcuts Grid */}
      {expanded && (
        <div className="px-4 pb-3">
          <div className="flex flex-wrap gap-1">
            {shortcuts.map((shortcut) => (
              <button
                key={shortcut.id}
                onClick={() => handleShortcut(shortcut.id)}
                disabled={loading !== null}
                data-testid={`ai-shortcut-${shortcut.id}-btn`}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all ${shortcut.color} border border-transparent hover:border-white/20 disabled:opacity-50`}
                title={shortcut.label}
              >
                {loading === shortcut.id ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <shortcut.icon className="w-3.5 h-3.5" />
                )}
                <span className="hidden sm:inline">{shortcut.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Result Panel */}
      {showResult && resultData && (
        <div className="border-t border-white/20 p-4 bg-[#0a0a0a]">
          {/* Onboarding Result */}
          {showResult === 'onboarding' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-white font-medium">Client Onboarding Extracted</h3>
                <button onClick={() => setShowResult(null)} className="text-gray-400 hover:text-white">
                  <X size={18} />
                </button>
              </div>
              
              <div className="grid grid-cols-2 gap-3 text-sm">
                {resultData.extraction?.client_name && (
                  <div><span className="text-gray-400">Client:</span> <span className="text-white">{resultData.extraction.client_name}</span></div>
                )}
                {resultData.extraction?.website && (
                  <div><span className="text-gray-400">Website:</span> <span className="text-blue-400">{resultData.extraction.website}</span></div>
                )}
                {resultData.extraction?.service_package && (
                  <div><span className="text-gray-400">Service:</span> <span className="text-white">{resultData.extraction.service_package}</span></div>
                )}
                {resultData.extraction?.assigned_team && (
                  <div><span className="text-gray-400">Team:</span> <span className="text-white">{resultData.extraction.assigned_team}</span></div>
                )}
              </div>

              {resultData.extraction?.action_items?.length > 0 && (
                <div>
                  <p className="text-gray-400 text-sm mb-2">Action Items:</p>
                  <div className="space-y-1">
                    {resultData.extraction.action_items.map((item, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-white">
                        <input type="checkbox" className="rounded" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="pt-3 border-t border-white/10">
                <p className="text-gray-400 text-sm mb-2">Slack Announcement Preview:</p>
                <textarea
                  value={editedContent}
                  onChange={(e) => setEditedContent(e.target.value)}
                  className="w-full h-40 bg-black border border-white/20 rounded-lg p-3 text-sm text-white resize-none"
                />
                
                <div className="flex items-center gap-3 mt-3">
                  <select
                    value={selectedChannel}
                    onChange={(e) => setSelectedChannel(e.target.value)}
                    className="flex-1 bg-black border border-white/20 rounded-lg px-3 py-2 text-sm text-white"
                  >
                    <option value="">Select Slack Channel...</option>
                    {slackChannels.map(ch => (
                      <option key={ch.id} value={ch.id}>#{ch.name}</option>
                    ))}
                  </select>
                  <Button
                    onClick={() => saveDraft('slack', editedContent)}
                    className="bg-purple-500 hover:bg-purple-600 text-white"
                    data-testid="save-slack-draft-btn"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Save as Draft
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  ⚠️ Draft will be saved for your review. Nothing will be sent automatically.
                </p>
              </div>
            </div>
          )}

          {/* Slack Draft Result */}
          {showResult === 'slack' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-white font-medium">Slack Announcement Draft</h3>
                <button onClick={() => setShowResult(null)} className="text-gray-400 hover:text-white">
                  <X size={18} />
                </button>
              </div>
              
              <textarea
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                className="w-full h-48 bg-black border border-white/20 rounded-lg p-3 text-sm text-white resize-none"
              />
              
              <div className="flex items-center gap-3">
                <select
                  value={selectedChannel}
                  onChange={(e) => setSelectedChannel(e.target.value)}
                  className="flex-1 bg-black border border-white/20 rounded-lg px-3 py-2 text-sm text-white"
                >
                  <option value="">Select Slack Channel...</option>
                  {slackChannels.map(ch => (
                    <option key={ch.id} value={ch.id}>#{ch.name}</option>
                  ))}
                </select>
                <Button
                  onClick={() => saveDraft('slack', editedContent)}
                  className="bg-purple-500 hover:bg-purple-600 text-white"
                  data-testid="save-slack-draft-btn"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Save as Draft
                </Button>
              </div>
              <p className="text-xs text-gray-500">
                ⚠️ Draft will be saved for your review. Nothing will be sent automatically.
              </p>
            </div>
          )}

          {/* Email Reply Result */}
          {showResult === 'reply' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-white font-medium">Email Reply Draft</h3>
                <button onClick={() => setShowResult(null)} className="text-gray-400 hover:text-white">
                  <X size={18} />
                </button>
              </div>
              
              <div className="text-sm text-gray-400">
                To: {email.from || email.sender}
              </div>
              <div className="text-sm text-gray-400">
                Subject: {resultData.subject}
              </div>
              
              <textarea
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                className="w-full h-40 bg-black border border-white/20 rounded-lg p-3 text-sm text-white resize-none"
              />
              
              <div className="flex items-center gap-3">
                <Button
                  onClick={() => saveDraft('email', editedContent)}
                  className="bg-green-500 hover:bg-green-600 text-white"
                  data-testid="save-email-draft-btn"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Save as Draft
                </Button>
              </div>
              <p className="text-xs text-gray-500">
                ⚠️ Draft will be saved for your review. Nothing will be sent automatically.
              </p>
            </div>
          )}

          {/* Quick Reply Result */}
          {showResult === 'quick' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-white font-medium">Quick Reply</h3>
                <button onClick={() => setShowResult(null)} className="text-gray-400 hover:text-white">
                  <X size={18} />
                </button>
              </div>
              
              {resultData.urls?.length > 0 && (
                <div className="text-sm">
                  <span className="text-gray-400">Detected URLs:</span>
                  <div className="mt-1 space-y-1">
                    {resultData.urls.map((url, i) => (
                      <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="block text-blue-400 hover:underline truncate">
                        {url}
                      </a>
                    ))}
                  </div>
                </div>
              )}
              
              <textarea
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                className="w-full h-24 bg-black border border-white/20 rounded-lg p-3 text-sm text-white resize-none"
              />
              
              <div className="flex items-center gap-3">
                <Button
                  onClick={() => saveDraft('email', editedContent)}
                  className="bg-amber-500 hover:bg-amber-600 text-white"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Save as Draft
                </Button>
              </div>
              <p className="text-xs text-gray-500">
                ⚠️ Draft will be saved for your review. Nothing will be sent automatically.
              </p>
            </div>
          )}

          {/* Action Items Result */}
          {showResult === 'actions' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-white font-medium">Extracted Action Items</h3>
                <button onClick={() => setShowResult(null)} className="text-gray-400 hover:text-white">
                  <X size={18} />
                </button>
              </div>
              
              {resultData.items?.length > 0 ? (
                <div className="space-y-2">
                  {resultData.items.map((item, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm">
                      <input type="checkbox" className="mt-1 rounded" />
                      <span className="text-white">{item}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-sm">No action items found in this email.</p>
              )}
              
              <Button
                onClick={() => {
                  // Copy to clipboard
                  navigator.clipboard.writeText(resultData.items?.join('\n') || '');
                  toast.success('Copied to clipboard');
                }}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10"
              >
                Copy All
              </Button>
            </div>
          )}

          {/* Template Result */}
          {showResult === 'template' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-white font-medium">Save as Template</h3>
                <button onClick={() => setShowResult(null)} className="text-gray-400 hover:text-white">
                  <X size={18} />
                </button>
              </div>
              
              <input
                type="text"
                placeholder="Template name..."
                id="template-name"
                className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-sm text-white"
              />
              
              <Button
                onClick={() => {
                  const name = document.getElementById('template-name')?.value;
                  saveTemplate(name);
                }}
                className="bg-pink-500 hover:bg-pink-600 text-white"
              >
                <FileText className="w-4 h-4 mr-2" />
                Save Template
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
